import React, { useEffect, useMemo, useState } from 'react';
import { BeratungsArt, Berater, CreateBeratungsterminRequest } from '../types';
import { beratungService, beraterService } from '../services/beratungService';
import { Icon, Spinner } from '../components/ui';

// Keyframes für die Kiosk-Animationen (scoped, ohne globale Tailwind-Config)
const GskStyles: React.FC = () => (
  <style>{`
    @keyframes gskFadeUp {from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:none}}
    @keyframes gskPop {0%{opacity:0;transform:scale(.6)}60%{opacity:1;transform:scale(1.08)}100%{transform:scale(1)}}
    @keyframes gskCheck {to{stroke-dashoffset:0}}
    @keyframes gskRing {0%{transform:scale(.5);opacity:.55}100%{transform:scale(2.6);opacity:0}}
    @keyframes gskFloat {0%{transform:translateY(30px) scale(1);opacity:0}15%{opacity:.55}100%{transform:translateY(-115vh) scale(1.35);opacity:0}}
    @keyframes gskGradient {0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
    @keyframes gskGlow {0%,100%{filter:drop-shadow(0 0 3px rgba(34,211,238,.45))}50%{filter:drop-shadow(0 0 11px rgba(34,211,238,.95))}}
    @keyframes gskShrink {from{width:100%}to{width:0%}}
    .gsk-fade-up{opacity:0;animation:gskFadeUp .7s cubic-bezier(.22,1,.36,1) forwards}
    @media (prefers-reduced-motion: reduce){
      .gsk-fade-up,[style*="gsk"]{animation:none!important;opacity:1!important}
    }
  `}</style>
);

// Aufsteigende Luftblasen (Pool-Thema) als dezenter Hintergrund
const Bubbles: React.FC<{ tint: 'light' | 'dark' }> = ({ tint }) => {
  const items = useMemo(
    () => Array.from({ length: 14 }, () => ({
      left: Math.random() * 100,
      size: 8 + Math.random() * 40,
      delay: Math.random() * 9,
      dur: 9 + Math.random() * 10,
    })),
    []
  );
  const cls = tint === 'dark' ? 'bg-white/10 border-white/20' : 'bg-brand-300/20 border-brand-300/30';
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {items.map((b, i) => (
        <span
          key={i}
          className={`absolute bottom-0 rounded-full border ${cls}`}
          style={{ left: `${b.left}%`, width: b.size, height: b.size, animation: `gskFloat ${b.dur}s linear ${b.delay}s infinite` }}
        />
      ))}
    </div>
  );
};

// GSK-IT Wortmarke (entspricht dem Logo aus dem gis-webseite-Projekt)
const PoweredBy: React.FC<{ dark?: boolean }> = ({ dark }) => (
  <div className={`fixed bottom-3 right-4 z-20 flex items-center gap-1.5 text-xs select-none ${dark ? 'text-white/80' : 'text-gray-400'}`}>
    <span>powered by</span>
    <span
      className={`font-bold tracking-tight flex items-center ${dark ? 'text-white' : 'text-gray-600'}`}
      style={{ animation: 'gskGlow 2.4s ease-in-out infinite' }}
    >
      GSK<span className="text-cyan-400 mx-px font-light">-</span>IT
      <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 ml-1 animate-pulse" />
    </span>
  </div>
);

const emptyForm: CreateBeratungsterminRequest = {
  vorname: '',
  nachname: '',
  telefon: '',
  email: '',
  plz: '',
  beratungsart: '' as BeratungsArt,
  bemerkung: '',
  beraterId: undefined,
};

const Beratungsanfrage: React.FC = () => {
  const [form, setForm] = useState<CreateBeratungsterminRequest>(emptyForm);
  const [berater, setBerater] = useState<Berater[]>([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    beraterService.getAll().then(setBerater).catch(() => {/* Auswahl bleibt leer */});
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Nur Ziffern zulassen (Telefon, PLZ)
  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value.replace(/\D/g, '') }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setDetailsOpen(false);
    setDone(false);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.nachname.trim() || !form.telefon.trim() || !form.plz.trim()) {
      setError('Bitte füllen Sie die Pflichtfelder (*) aus.');
      return;
    }
    if (!form.beratungsart) {
      setError('Bitte wählen Sie die Art der Beratung.');
      return;
    }

    setSubmitting(true);
    try {
      await beratungService.create({
        vorname: form.vorname?.trim() || undefined,
        nachname: form.nachname.trim(),
        telefon: form.telefon.trim(),
        email: form.email?.trim() || undefined,
        plz: form.plz.trim(),
        beratungsart: form.beratungsart,
        bemerkung: form.bemerkung?.trim() || undefined,
        beraterId: form.beraterId ?? undefined,
      });
      setDone(true);
      // Bestätigung 5 Sekunden zeigen, dann automatisch zum Formular zurück
      window.setTimeout(resetForm, 5000);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Die Anfrage konnte nicht gesendet werden. Bitte erneut versuchen.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-5 py-3 text-lg landscape:text-xl border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500';
  const labelClass = 'block text-base landscape:text-lg font-medium text-gray-700 mb-1.5';

  if (done) {
    return (
      <div
        className="fixed inset-0 flex flex-col items-center justify-center p-6 text-center overflow-hidden"
        style={{
          background: 'linear-gradient(125deg,#0c1c30,#143357,#1a4f8c,#3b6bb5,#143357)',
          backgroundSize: '300% 300%',
          animation: 'gskGradient 12s ease infinite',
        }}
      >
        <GskStyles />
        <Bubbles tint="dark" />

        <div
          className="relative bg-white rounded-3xl shadow-2xl p-10 sm:p-12 max-w-lg w-full"
          style={{ animation: 'gskPop .6s cubic-bezier(.22,1,.36,1) both' }}
        >
          <img src="/hp-logo-line.png" alt="HeinzePool" className="h-9 w-auto mx-auto mb-8 gsk-fade-up" style={{ animationDelay: '.15s' }} />

          {/* Rippelnde Ringe + sich zeichnender Haken */}
          <div className="relative mx-auto mb-7 w-24 h-24">
            <span className="absolute inset-0 rounded-full bg-green-400/30" style={{ animation: 'gskRing 1.8s ease-out .4s infinite' }} />
            <span className="absolute inset-0 rounded-full bg-green-400/20" style={{ animation: 'gskRing 1.8s ease-out .9s infinite' }} />
            <div className="relative w-24 h-24 rounded-full bg-green-100 flex items-center justify-center" style={{ animation: 'gskPop .5s .25s both' }}>
              <svg className="w-12 h-12 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round">
                <path d="M4.5 12.75l6 6 9-13.5" style={{ strokeDasharray: 30, strokeDashoffset: 30, animation: 'gskCheck .5s ease-out .55s forwards' }} />
              </svg>
            </div>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-3 gsk-fade-up" style={{ animationDelay: '.4s' }}>Vielen Dank!</h1>
          <p className="text-lg text-gray-600 mb-8 gsk-fade-up" style={{ animationDelay: '.5s' }}>
            Ihre Anfrage wurde erfasst. Wir melden uns in Kürze bei Ihnen, um einen Beratungstermin zu vereinbaren.
          </p>
          <button
            onClick={resetForm}
            className="w-full py-4 px-6 text-lg font-semibold rounded-lg text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 gsk-fade-up"
            style={{ animationDelay: '.6s' }}
          >
            Neue Anfrage
          </button>

          {/* Fortschrittsbalken: läuft in 5 s ab, dann automatischer Rücksprung */}
          <div className="mt-6 h-1 w-full bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-brand-500 rounded-full" style={{ animation: 'gskShrink 5s linear forwards' }} />
          </div>
        </div>
        <PoweredBy dark />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 overflow-y-auto bg-gradient-to-b from-brand-50 to-gray-50">
      <GskStyles />
      <Bubbles tint="light" />
      <div className="relative z-10 min-h-full flex flex-col items-center justify-center gap-4 landscape:gap-5 p-4 landscape:py-5">
        <div className="flex flex-col items-center text-center shrink-0 gsk-fade-up">
          <img src="/hp-logo-line.png" alt="HeinzePool" className="h-10 landscape:h-12 w-auto" />
          <h1 className="mt-3 text-3xl landscape:text-4xl font-extrabold text-gray-900">
            Beratungstermin vereinbaren
          </h1>
          <p className="mt-1.5 text-gray-600 text-lg">
            Hinterlassen Sie Ihre Kontaktdaten – wir melden uns bei Ihnen.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="w-full max-w-4xl bg-white rounded-3xl shadow-lg p-6 landscape:p-7 space-y-4 gsk-fade-up" style={{ animationDelay: '.12s' }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 landscape:gap-6">
              <div>
                <label htmlFor="vorname" className={labelClass}>Vorname</label>
                <input id="vorname" name="vorname" type="text" autoComplete="off"
                  className={inputClass} value={form.vorname} onChange={handleChange} disabled={submitting} />
              </div>
              <div>
                <label htmlFor="nachname" className={labelClass}>Nachname *</label>
                <input id="nachname" name="nachname" type="text" autoComplete="off" required
                  className={inputClass} value={form.nachname} onChange={handleChange} disabled={submitting} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 landscape:gap-6">
              <div>
                <label htmlFor="telefon" className={labelClass}>Telefon *</label>
                <input id="telefon" name="telefon" type="tel" inputMode="numeric" pattern="[0-9]*" autoComplete="off" required
                  className={inputClass} value={form.telefon} onChange={handleNumericChange} disabled={submitting} />
              </div>
              <div>
                <label htmlFor="plz" className={labelClass}>PLZ *</label>
                <input id="plz" name="plz" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={5} autoComplete="off" required
                  className={inputClass} value={form.plz} onChange={handleNumericChange} disabled={submitting} />
              </div>
            </div>

            <div>
              <label htmlFor="email" className={labelClass}>E-Mail</label>
              <input id="email" name="email" type="email" inputMode="email" autoComplete="off"
                className={inputClass} value={form.email} onChange={handleChange} disabled={submitting} />
            </div>

            <div>
              <span className={labelClass}>Art der Beratung *</span>
              <div className="grid grid-cols-3 gap-3 landscape:gap-4 mt-1.5">
                {([
                  { value: 'NEUBAU', label: 'Neubau' },
                  { value: 'SANIERUNG', label: 'Sanierung' },
                  { value: 'SERVICE_BESTAND', label: 'Service für Bestand' },
                ] as { value: BeratungsArt; label: string }[]).map((opt) => {
                  const active = form.beratungsart === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setForm((prev) => ({ ...prev, beratungsart: opt.value }))}
                      disabled={submitting}
                      className={`py-3.5 px-3 text-base landscape:text-lg font-semibold rounded-xl border-2 transition-all duration-200 active:scale-95 ${
                        active
                          ? 'border-brand-600 bg-brand-600 text-white shadow-lg shadow-brand-600/30 scale-[1.03]'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-brand-400 hover:-translate-y-0.5'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-gray-100 pt-2">
              <button
                type="button"
                onClick={() => setDetailsOpen((o) => !o)}
                aria-expanded={detailsOpen}
                className="flex items-center gap-1.5 text-base landscape:text-lg font-medium text-gray-600 hover:text-brand-600 transition-colors"
              >
                <Icon.ChevronRight className={`w-5 h-5 transition-transform duration-200 ${detailsOpen ? 'rotate-90' : ''}`} />
                Weitere Angaben (optional)
              </button>

              {detailsOpen && (
                <div className="ui-aufklappen mt-4 space-y-4">
                  {berater.length > 0 && (
                    <div>
                      <label htmlFor="beraterId" className={labelClass}>Berater</label>
                      <select
                        id="beraterId"
                        name="beraterId"
                        className={inputClass}
                        value={form.beraterId ?? ''}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            beraterId: e.target.value ? Number(e.target.value) : undefined,
                          }))
                        }
                        disabled={submitting}
                      >
                        <option value="">– bitte wählen –</option>
                        {berater.map((b) => (
                          <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label htmlFor="bemerkung" className={labelClass}>Bemerkung</label>
                    <textarea
                      id="bemerkung"
                      name="bemerkung"
                      rows={3}
                      className={`${inputClass} resize-none`}
                      value={form.bemerkung}
                      onChange={(e) => setForm((prev) => ({ ...prev, bemerkung: e.target.value }))}
                      disabled={submitting}
                    />
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-red-700 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-4 px-6 text-xl font-semibold rounded-xl text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-lg hover:shadow-brand-600/30 active:scale-[0.98] flex items-center justify-center gap-2"
            >
              {submitting && (
                <Spinner className="h-5 w-5" />
              )}
              {submitting ? 'Wird gesendet…' : 'Anfrage absenden'}
            </button>
        </form>
      </div>
      <PoweredBy />
    </div>
  );
};

export default Beratungsanfrage;
